(() => {
  const SWATCH_SELECTOR = '[productimage]';
  const IMAGE_SELECTOR = '.card__media img';
  const SELECTED_CLASS = 'variant-hover-selected';
  const FADE_DURATION = 180;

  const preloadedImages = new Set();
  let changeCounter = 0;

  /**
   * Findet die zugehörige Produktkarte.
   */
  const findCard = (element) => {
    return (
      element?.closest('.card-wrapper') ||
      element?.closest('.grid__item') ||
      null
    );
  };

  /**
   * Findet das sichtbare Hauptbild der Produktkarte.
   */
  const findCardImage = (element) => {
    const card = findCard(element);
    if (!card) return null;

    return card.querySelector(IMAGE_SELECTOR);
  };

  /**
   * Speichert das ursprüngliche freigestellte Produktbild.
   */
  const saveOriginalImage = (image) => {
    if (!image) return;

    if (image.dataset.variantHoverOriginalSaved === 'true') {
      return;
    }

    image.dataset.variantHoverOriginalSrc =
      image.getAttribute('src') || '';

    image.dataset.variantHoverOriginalSrcset =
      image.getAttribute('srcset') || '';

    image.dataset.variantHoverOriginalSizes =
      image.getAttribute('sizes') || '';

    image.dataset.variantHoverOriginalSaved = 'true';
  };

  /**
   * Speichert alle ursprünglichen Produktbilder.
   */
  const saveAllOriginalImages = (root = document) => {
    if (!(root instanceof Element || root === document)) return;

    if (
      root instanceof Element &&
      root.matches(IMAGE_SELECTOR)
    ) {
      saveOriginalImage(root);
    }

    root.querySelectorAll(IMAGE_SELECTOR).forEach((image) => {
      saveOriginalImage(image);
    });
  };

  /**
   * Lädt ein Variantenbild vor.
   */
  const preloadImage = (imageUrl) => {
    if (!imageUrl || preloadedImages.has(imageUrl)) {
      return Promise.resolve();
    }

    preloadedImages.add(imageUrl);

    return new Promise((resolve) => {
      const preload = new Image();

      preload.onload = resolve;
      preload.onerror = resolve;
      preload.src = imageUrl;

      if (preload.complete) {
        resolve();
      }
    });
  };

  /**
   * Setzt die Bildattribute.
   */
  const setImageAttributes = (image, imageUrl) => {
    image.setAttribute('src', imageUrl);
    image.setAttribute('srcset', imageUrl);
    image.removeAttribute('sizes');
  };

  /**
   * Tauscht das Produktbild mit einer sanften Animation.
   */
  const changeImage = async (
    image,
    imageUrl,
    useAnimation = true
  ) => {
    if (!image || !imageUrl) return;

    saveOriginalImage(image);

    const currentChange = ++changeCounter;
    image.dataset.variantHoverChange = String(currentChange);

    await preloadImage(imageUrl);

    if (
      image.dataset.variantHoverChange !== String(currentChange)
    ) {
      return;
    }

    if (!useAnimation) {
      setImageAttributes(image, imageUrl);
      image.style.opacity = '1';
      return;
    }

    const currentSrc = image.getAttribute('src') || '';

    if (currentSrc === imageUrl) {
      image.style.opacity = '1';
      return;
    }

    image.style.opacity = '0';

    window.setTimeout(() => {
      if (
        image.dataset.variantHoverChange !== String(currentChange)
      ) {
        return;
      }

      setImageAttributes(image, imageUrl);

      requestAnimationFrame(() => {
        image.style.opacity = '1';
      });
    }, FADE_DURATION);
  };

  /**
   * Zeigt das Bild eines Farbpunkts.
   */
  const showVariantImage = (
    swatch,
    useAnimation = true
  ) => {
    if (!swatch) return;

    const imageUrl = swatch.getAttribute('productimage');
    if (!imageUrl) return;

    const image = findCardImage(swatch);
    if (!image) return;

    const card = findCard(swatch);

    if (card) {
      card.dataset.variantHoverActive = 'true';
      card.dataset.variantHoverUrl = imageUrl;
    }

    changeImage(image, imageUrl, useAnimation);
  };

  /**
   * Markiert genau den tatsächlich angeklickten Farbpunkt.
   */
 const markSelectedSwatch = (swatch) => {
  const card = findCard(swatch);
  if (!card) return;

  card.querySelectorAll(SWATCH_SELECTOR).forEach((item) => {
    item.classList.remove(SELECTED_CLASS);
    item.style.removeProperty('--selected-swatch-color');
  });

  const colorElement =
    swatch.querySelector('[style*="background-color"]') ||
    swatch.querySelector('.globo-border-color-swatch') ||
    swatch.querySelector('.swatch-anchor');

  if (colorElement) {
    const swatchColor =
      window.getComputedStyle(colorElement).backgroundColor;

    swatch.style.setProperty(
      '--selected-swatch-color',
      swatchColor
    );
  }

  swatch.classList.add(SELECTED_CLASS);
};

  /**
   * Stellt das ursprüngliche freigestellte Produktbild wieder her.
   */
  const restoreOriginalImage = (swatch) => {
    const image = findCardImage(swatch);

    if (
      !image ||
      image.dataset.variantHoverOriginalSaved !== 'true'
    ) {
      return;
    }

    const card = findCard(swatch);

    /*
     * Nach einem Klick bleibt die ausgewählte Variante sichtbar.
     */
    if (card?.dataset.variantHoverClicked === 'true') {
      return;
    }

    const originalSrc =
      image.dataset.variantHoverOriginalSrc || '';

    if (!originalSrc) return;

    const currentChange = ++changeCounter;
    image.dataset.variantHoverChange = String(currentChange);
    image.style.opacity = '0';

    window.setTimeout(() => {
      if (
        image.dataset.variantHoverChange !== String(currentChange)
      ) {
        return;
      }

      image.setAttribute('src', originalSrc);

      const originalSrcset =
        image.dataset.variantHoverOriginalSrcset || '';

      const originalSizes =
        image.dataset.variantHoverOriginalSizes || '';

      if (originalSrcset) {
        image.setAttribute('srcset', originalSrcset);
      } else {
        image.removeAttribute('srcset');
      }

      if (originalSizes) {
        image.setAttribute('sizes', originalSizes);
      } else {
        image.removeAttribute('sizes');
      }

      if (card) {
        delete card.dataset.variantHoverActive;
        delete card.dataset.variantHoverUrl;
      }

      requestAnimationFrame(() => {
        image.style.opacity = '1';
      });
    }, FADE_DURATION);
  };

  /**
   * Fügt die Übergangsanimation für Produktbilder hinzu.
   */
  const addTransitionStyles = () => {
    if (document.getElementById('variant-hover-styles')) return;

    const style = document.createElement('style');
    style.id = 'variant-hover-styles';

    style.textContent = `
      .card__media img {
        transition: opacity ${FADE_DURATION}ms ease-in-out;
      }

      @media (prefers-reduced-motion: reduce) {
        .card__media img {
          transition: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  };

  /**
   * Lädt alle Variantenbilder im Hintergrund vor.
   */
  const preloadAllVariantImages = (root = document) => {
    if (!(root instanceof Element || root === document)) return;

    if (
      root instanceof Element &&
      root.matches(SWATCH_SELECTOR)
    ) {
      preloadImage(root.getAttribute('productimage'));
    }

    root.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      preloadImage(swatch.getAttribute('productimage'));
    });
  };

  /**
   * Desktop: Variantenbild beim Überfahren anzeigen.
   */
  document.addEventListener('mouseover', (event) => {
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;

    if (swatch.contains(event.relatedTarget)) return;

    showVariantImage(swatch);
  });

  /**
   * Desktop: Beim Verlassen das Ausgangsbild anzeigen,
   * solange keine Farbe angeklickt wurde.
   */
  document.addEventListener('mouseout', (event) => {
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;

    if (swatch.contains(event.relatedTarget)) return;

    const nextSwatch =
      event.relatedTarget?.closest?.(SWATCH_SELECTOR);

    const currentCard = findCard(swatch);
    const nextCard = nextSwatch ? findCard(nextSwatch) : null;

    if (nextSwatch && currentCard === nextCard) {
      return;
    }

    restoreOriginalImage(swatch);
  });

  /**
   * Klick oder Fingertipp:
   * Bild und optische Markierung bleiben auf der gewählten Farbe.
   */
  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;

    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;

    const imageUrl = swatch.getAttribute('productimage');
    const card = findCard(swatch);

    if (!imageUrl || !card) return;

    card.dataset.variantHoverClicked = 'true';
    card.dataset.variantHoverUrl = imageUrl;

    /*
     * Nur der wirklich angeklickte Farbpunkt wird vergrößert.
     */
    markSelectedSwatch(swatch);

    /*
     * Sofort sichtbare Reaktion.
     */
    showVariantImage(swatch);

    /*
     * Nach der GLO-Aktualisierung das korrekte Bild erneut setzen.
     */
    window.setTimeout(() => {
      const currentImage = card.querySelector(IMAGE_SELECTOR);
      if (!currentImage) return;

      changeImage(currentImage, imageUrl, false);
    }, 100);

    /*
     * Zweite Absicherung für langsamere Aktualisierungen.
     */
    window.setTimeout(() => {
      const currentImage = card.querySelector(IMAGE_SELECTOR);
      if (!currentImage) return;

      changeImage(currentImage, imageUrl, false);
    }, 350);
  });

  /**
   * Dynamisch nachgeladene Elemente erkennen.
   */
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        saveAllOriginalImages(node);
        preloadAllVariantImages(node);
      });
    });
  });

  const initialize = () => {
    addTransitionStyles();

    /*
     * Beim ersten Laden wird bewusst kein Farbpunkt von unserem
     * Code als ausgewählt markiert.
     */
    document.querySelectorAll(`.${SELECTED_CLASS}`).forEach((item) => {
      item.classList.remove(SELECTED_CLASS);
    });

    /*
     * Zuerst die freigestellten Ausgangsbilder sichern.
     */
    saveAllOriginalImages();

    /*
     * Variantenbilder anschließend im Hintergrund vorladen.
     */
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        preloadAllVariantImages();
      });
    } else {
      window.setTimeout(() => {
        preloadAllVariantImages();
      }, 500);
    }

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
/* GLO-Farbpunkte auf Produktkarten über den Preis verschieben */
(() => {
  const CARD_INFO_SELECTOR =
    '.card__information.globo-swatch-product-item';

  const SWATCH_LIST_SELECTOR =
    ':scope > .swatches-globo--list';

  const PRICE_BLOCK_SELECTOR =
    ':scope > .card-information';

  const moveSwatches = (root = document) => {
    const cardInfos = [];

    if (
      root instanceof Element &&
      root.matches(CARD_INFO_SELECTOR)
    ) {
      cardInfos.push(root);
    }

    if (root.querySelectorAll) {
      cardInfos.push(
        ...root.querySelectorAll(CARD_INFO_SELECTOR)
      );
    }

    cardInfos.forEach((cardInfo) => {
      const swatchList =
        cardInfo.querySelector(SWATCH_LIST_SELECTOR);

      const priceBlock =
        cardInfo.querySelector(PRICE_BLOCK_SELECTOR);

      if (!swatchList || !priceBlock) return;

      /*
       * Nur verschieben, wenn die Swatches noch nicht
       * direkt vor dem Preisblock stehen.
       */
      if (swatchList.nextElementSibling !== priceBlock) {
        cardInfo.insertBefore(swatchList, priceBlock);
      }
    });
  };

  const initializeSwatchPosition = () => {
    moveSwatches();

    /*
     * GLO lädt die Farbpunkte nachträglich.
     * Deshalb werden neu eingefügte Elemente beobachtet.
     */
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (
            node.matches?.('.swatches-globo--list') ||
            node.querySelector?.('.swatches-globo--list')
          ) {
            shouldCheck = true;
          }
        });
      });

      if (shouldCheck) {
        moveSwatches();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeSwatchPosition
    );
  } else {
    initializeSwatchPosition();
  }
})();
/* Produktseite: Klick auf Variantenbild wählt passenden GLO-Farbswatch */
(() => {
  document.addEventListener('click', (event) => {
    const thumbnail = event.target.closest(
      '.thumbnail-list__item button.thumbnail'
    );

    if (!thumbnail) return;

    const listItem = thumbnail.closest('.thumbnail-list__item');
    if (!listItem) return;

    const target = listItem.getAttribute('data-target');
    if (!target) return;

    /*
     * Media-ID aus dem Dawn-Thumbnail auslesen.
     * Beispiel:
     * template--...__main-69567140168020
     */
    const mediaId = target.split('-').pop();
    if (!mediaId) return;

    /*
     * Produktvarianten direkt aus GLO verwenden.
     */
    const variants =
      window.GloboSwatchConfig?.product?.variants;

    if (!Array.isArray(variants)) return;

    /*
     * Variante suchen, deren featured_media.id
     * dem angeklickten Galeriebild entspricht.
     */
    const matchingVariant = variants.find((variant) => {
      return (
        variant.featured_media &&
        String(variant.featured_media.id) === String(mediaId)
      );
    });

    if (!matchingVariant) return;

    const color = matchingVariant.option1;
    if (!color) return;

    /*
     * GLO-Farbgruppe "Farbe" suchen.
     */
    const colorGroup = Array.from(
      document.querySelectorAll('.swatch--gl')
    ).find((group) => {
      return group.querySelector(
        '.name-option[data-name="Farbe"]'
      );
    });

    if (!colorGroup) return;

    /*
     * Passenden GLO-Radiobutton anhand des Farbnamens finden.
     */
    const matchingRadio = Array.from(
      colorGroup.querySelectorAll('input[type="radio"]')
    ).find((radio) => radio.value === color);

    if (!matchingRadio || matchingRadio.checked) return;

    /*
     * Echten GLO-Klick auslösen.
     * Dadurch werden Farbe, Größen und Variante
     * von GLO selbst aktualisiert.
     */
    const option = matchingRadio.closest('li[data-variantid]');
    const label = option?.querySelector('label');

    if (label) {
      label.click();
    } else {
      matchingRadio.click();
    }
  });
})();/* Produktseite: Bilder der gewählten Farbe als Gruppe nach vorne sortieren */
(() => {

  /*
   * Ermittelt automatisch, welche Variantenoption die Farbe ist.
   * Funktioniert also z. B. sowohl bei:
   * option1 = Farbe / option2 = Größe
   * als auch bei:
   * option1 = Größe / option2 = Farbe
   */
  function getColorOptionIndex() {
    const colorGroup = Array.from(
      document.querySelectorAll('.swatch--gl')
    ).find((group) =>
      group.querySelector('.name-option[data-name="Farbe"]')
    );

    if (!colorGroup) return null;

    const radio = colorGroup.querySelector(
      'input[data-globoswatchindex]'
    );

    if (!radio) return null;

    const match = radio
      .getAttribute('data-globoswatchindex')
      ?.match(/option(\d+)/);

    return match ? Number(match[1]) : null;
  }

  function getColorGroups() {
    const product = window.GloboSwatchConfig?.product;
    const colorOptionIndex = getColorOptionIndex();

    if (
      !product ||
      !Array.isArray(product.variants) ||
      !Array.isArray(product.media) ||
      !colorOptionIndex
    ) {
      return [];
    }

    const colorKey = `option${colorOptionIndex}`;

    /*
     * Pro Farbe das von Shopify hinterlegte
     * Varianten-Hauptbild bestimmen.
     */
    const colors = product.variants
      .filter(
        (variant, index, variants) =>
          variants.findIndex(
            (item) => item[colorKey] === variant[colorKey]
          ) === index
      )
      .map((variant) => {
        const mediaId = variant.featured_media?.id;

        const media = product.media.find(
          (item) => String(item.id) === String(mediaId)
        );

        return {
          color: variant[colorKey],
          start: media?.position
        };
      })
      .filter(
        (item) =>
          item.color &&
          Number.isFinite(Number(item.start))
      )
      .sort((a, b) => a.start - b.start);

    /*
     * Doppelte Startpositionen entfernen.
     */
    const uniqueColors = colors.filter(
      (item, index, array) =>
        array.findIndex(
          (other) =>
            String(other.start) === String(item.start)
        ) === index
    );

    /*
     * Aus den Startpositionen Bildgruppen bilden.
     */
    return uniqueColors.map((item, index) => ({
      color: item.color,
      start: Number(item.start),
      end:
        index < uniqueColors.length - 1
          ? Number(uniqueColors[index + 1].start) - 1
          : product.media.length
    }));
  }

  function sortThumbnailsByColor(color) {
    const groups = getColorGroups();

    const selectedGroup = groups.find(
      (group) => group.color === color
    );

    if (!selectedGroup) return;

    const thumbnailList = document.querySelector(
      '[id^="Slider-Thumbnails-"]'
    );

    if (!thumbnailList) return;

    const thumbnails = Array.from(
      thumbnailList.querySelectorAll(
        '.thumbnail-list__item'
      )
    );

    if (!thumbnails.length) return;

    const selected = [];
    const others = [];

    thumbnails.forEach((thumbnail) => {
      const position = Number(
        thumbnail.getAttribute('data-media-position')
      );

      if (
        position >= selectedGroup.start &&
        position <= selectedGroup.end
      ) {
        selected.push(thumbnail);
      } else {
        others.push(thumbnail);
      }
    });

    [...selected, ...others].forEach((thumbnail) => {
      thumbnailList.appendChild(thumbnail);
    });
  }

  /*
   * Auf einen echten GLO-Farbwechsel reagieren.
   */
  document.addEventListener('click', (event) => {
    const colorGroup = event.target.closest('.swatch--gl');

    if (
      !colorGroup ||
      !colorGroup.querySelector(
        '.name-option[data-name="Farbe"]'
      )
    ) {
      return;
    }

    const radio =
      event.target.closest('input[type="radio"]') ||
      event.target
        .closest('li')
        ?.querySelector('input[type="radio"]');

    if (!radio) return;

    setTimeout(() => {
      sortThumbnailsByColor(radio.value);
    }, 50);
  });

})();